import { Suspense, lazy, type ReactElement } from 'react';
import { NavigationProvider, useNavigation } from './app/navigation';
import type { RouteId } from './app/routes';
import { AppShell } from './components/layout/AppShell';
import { BootScreen } from './components/layout/BootScreen';
import { SignInScreen } from './components/layout/SignInScreen';
import { RoleSwitch } from './components/dev/RoleSwitch';
import { ProfilePicker } from './components/features/ProfilePicker';
import { GameProvider } from './game/store';
import { useHeartbeat } from './lib/useHeartbeat';
import { DailyWheelPage } from './pages/DailyWheelPage';
import { HomePage } from './pages/HomePage';
import { HowToPlayPage } from './pages/HowToPlayPage';
import { ProfilePage } from './pages/ProfilePage';
import { RewardsPage } from './pages/RewardsPage';
import { ShopPage } from './pages/ShopPage';
import { ScratchPage } from './pages/ScratchPage';
import { WheelPage } from './pages/WheelPage';
import { SessionProvider, useSession } from './state/session';

/**
 * The owner console is a separate chunk, fetched only once the server has said
 * `owner`. It carries the code that reads real odds, and shipping it to every
 * player told anyone who opened the bundle that two sets of odds exist. See
 * `dashboard/OwnerApp.tsx`.
 */
const OwnerApp = lazy(() => import('./dashboard/OwnerApp'));

const PAGES: Record<RouteId, () => ReactElement | null> = {
  home: HomePage,
  wheel: WheelPage,
  scratch: ScratchPage,
  daily: DailyWheelPage,
  shop: ShopPage,
  rewards: RewardsPage,
  howto: HowToPlayPage,
  profile: ProfilePage,
};

function CurrentPage() {
  const { route } = useNavigation();
  const Page = PAGES[route];
  return <Page />;
}

/** The player-facing game interface — everyone who is not the owner. */
function GameApp() {
  // Only players are tracked, and this is the only place they mount — so the
  // owner and managers, who never reach here, never count toward client time.
  useHeartbeat();
  return (
    <NavigationProvider>
      <AppShell>
        <CurrentPage />
      </AppShell>
    </NavigationProvider>
  );
}

function AuthenticatedApp() {
  const { status, error, role, mode, activeProfileId } = useSession();

  // Studio-only states: there is no session yet, or the account that signed in
  // turned out to be a player. Both end at the front door rather than the app.
  if (status === 'signed-out' || status === 'denied') {
    return <SignInScreen />;
  }

  if (status !== 'ready') {
    return <BootScreen error={error} />;
  }

  // The backend decides the role; a client can never be routed to the Dashboard.
  // Managers mount it too — the server scopes every one of their requests to
  // the single profile the registry hands them, so the console they see is
  // their own profile's and nobody else's.
  //
  // The branches get different stores, and that is a boundary rather than a
  // convenience: `EconomyProvider` holds the real odds and every player's
  // balance, `GameProvider` holds one player's public view. A player never
  // mounts the store that knows too much.
  //
  // Both providers are keyed on the active profile: each profile is a separate
  // economy, so switching one remounts the store rather than letting a mirror
  // of the previous economy answer for the next.
  //
  // On the studio there is no game branch at all: the server refuses to open a
  // session for a client, so reaching it would mean the guarantee had already
  // failed. Rendering the front door instead of the game keeps the two answers
  // from ever disagreeing.
  const managing = role === 'owner' || role === 'manager';
  if (mode === 'web' && !managing) {
    return <SignInScreen />;
  }

  return (
    <>
      {managing ? (
        <Suspense fallback={<BootScreen error={null} />}>
          <OwnerApp key={activeProfileId ?? 'none'} />
        </Suspense>
      ) : activeProfileId === null ? (
        <ProfilePicker />
      ) : (
        <GameProvider key={activeProfileId}>
          <GameApp />
        </GameProvider>
      )}
      {mode === 'preview' && <RoleSwitch />}
    </>
  );
}

export function App() {
  return (
    <SessionProvider>
      <AuthenticatedApp />
    </SessionProvider>
  );
}
